import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown, 
  Star, 
  TrendingUp, 
  TrendingDown,
  Filter,
  Search,
  Users,
  Trophy,
  Sparkles
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Prospect {
  id: number;
  name: string;
  position: string;
  team: string;
  league: string;
  nationality: string;
  height: string;
  weight: string;
  birthDate: string;
  shoots: string;
  rankings: {
    consensus: number;
    eliteProspects: number;
    tsnCraigButton: number;
    bobMckenzie: number;
    thehockeywriters: number;
    fcHockey: number;
  };
  trend: "up" | "down" | "stable";
  tier: "Elite" | "Top" | "First Round" | "Second Round" | "Later";
  scoutingNotes: string;
}

type SortField = "consensus" | "eliteProspects" | "tsnCraigButton" | "bobMckenzie" | "thehockeywriters" | "fcHockey" | "name";
type SortDirection = "asc" | "desc";

const prospects: Prospect[] = [
  {
    id: 1,
    name: "James Hagens",
    position: "C",
    team: "Boston College",
    league: "NCAA",
    nationality: "USA",
    height: "5'11\"",
    weight: "175",
    birthDate: "2006-09-27",
    shoots: "L",
    rankings: { consensus: 1, eliteProspects: 1, tsnCraigButton: 1, bobMckenzie: 1, thehockeywriters: 1, fcHockey: 1 },
    trend: "stable",
    tier: "Elite",
    scoutingNotes: "Elite playmaking center with exceptional vision and hockey IQ. Dynamic skater with quick acceleration.",
  },
  {
    id: 2,
    name: "Porter Martone",
    position: "RW",
    team: "Brampton Steelheads",
    league: "OHL",
    nationality: "CAN",
    height: "6'3\"",
    weight: "205",
    birthDate: "2007-01-12",
    shoots: "R",
    rankings: { consensus: 2, eliteProspects: 3, tsnCraigButton: 2, bobMckenzie: 2, thehockeywriters: 3, fcHockey: 2 },
    trend: "up",
    tier: "Elite",
    scoutingNotes: "Power forward with excellent size and skill combination. Physical presence with soft hands.",
  },
  {
    id: 3,
    name: "Matthew Schaefer",
    position: "LD",
    team: "Erie Otters",
    league: "OHL",
    nationality: "CAN",
    height: "6'1\"",
    weight: "190",
    birthDate: "2007-03-15",
    shoots: "L",
    rankings: { consensus: 3, eliteProspects: 2, tsnCraigButton: 3, bobMckenzie: 3, thehockeywriters: 2, fcHockey: 3 },
    trend: "stable",
    tier: "Elite",
    scoutingNotes: "Elite skating defenseman with tremendous puck-moving ability. Exceptional edge work and gap control.",
  },
  {
    id: 4,
    name: "Roger Mäntylä",
    position: "LW",
    team: "Djurgårdens IF",
    league: "SHL",
    nationality: "SWE",
    height: "6'0\"",
    weight: "183",
    birthDate: "2006-05-20",
    shoots: "L",
    rankings: { consensus: 4, eliteProspects: 4, tsnCraigButton: 5, bobMckenzie: 4, thehockeywriters: 4, fcHockey: 5 },
    trend: "up",
    tier: "Elite",
    scoutingNotes: "Dynamic Swedish forward with elite offensive instincts. Dangerous shooter with quick release.",
  },
  {
    id: 5,
    name: "Cameron Schmidt",
    position: "C",
    team: "Vancouver Giants",
    league: "WHL",
    nationality: "CAN",
    height: "6'2\"",
    weight: "195",
    birthDate: "2007-02-08",
    shoots: "R",
    rankings: { consensus: 5, eliteProspects: 5, tsnCraigButton: 4, bobMckenzie: 6, thehockeywriters: 5, fcHockey: 4 },
    trend: "stable",
    tier: "Elite",
    scoutingNotes: "Two-way center with excellent compete level. Strong in faceoff circle with responsible defensive play.",
  },
  {
    id: 6,
    name: "Anton Frondell",
    position: "C",
    team: "Rögle BK",
    league: "SHL",
    nationality: "SWE",
    height: "6'1\"",
    weight: "180",
    birthDate: "2006-08-14",
    shoots: "L",
    rankings: { consensus: 6, eliteProspects: 6, tsnCraigButton: 7, bobMckenzie: 5, thehockeywriters: 6, fcHockey: 7 },
    trend: "up",
    tier: "Top",
    scoutingNotes: "Skilled playmaker with excellent hockey sense. Creative with the puck and excellent vision.",
  },
  {
    id: 7,
    name: "Jackson Smith",
    position: "RD",
    team: "Tri-City Americans",
    league: "WHL",
    nationality: "CAN",
    height: "6'3\"",
    weight: "198",
    birthDate: "2007-04-22",
    shoots: "R",
    rankings: { consensus: 7, eliteProspects: 8, tsnCraigButton: 6, bobMckenzie: 7, thehockeywriters: 8, fcHockey: 6 },
    trend: "stable",
    tier: "Top",
    scoutingNotes: "Physical defenseman with excellent size. Strong defensive presence with improving offensive game.",
  },
  {
    id: 8,
    name: "Caleb Desnoyers",
    position: "C",
    team: "Moncton Wildcats",
    league: "QMJHL",
    nationality: "CAN",
    height: "6'0\"",
    weight: "185",
    birthDate: "2006-12-03",
    shoots: "L",
    rankings: { consensus: 8, eliteProspects: 7, tsnCraigButton: 8, bobMckenzie: 9, thehockeywriters: 7, fcHockey: 9 },
    trend: "down",
    tier: "Top",
    scoutingNotes: "Versatile center with strong two-way game. Excellent motor and compete level every shift.",
  },
  {
    id: 9,
    name: "Victor Eklund",
    position: "LW",
    team: "Frölunda HC",
    league: "SHL",
    nationality: "SWE",
    height: "5'10\"",
    weight: "172",
    birthDate: "2007-01-30",
    shoots: "L",
    rankings: { consensus: 9, eliteProspects: 10, tsnCraigButton: 9, bobMckenzie: 8, thehockeywriters: 10, fcHockey: 8 },
    trend: "up",
    tier: "Top",
    scoutingNotes: "Highly skilled Swedish winger. Excellent puck skills and offensive creativity.",
  },
  {
    id: 10,
    name: "Lucas Pettersson",
    position: "C",
    team: "HV71",
    league: "SHL",
    nationality: "SWE",
    height: "6'1\"",
    weight: "178",
    birthDate: "2006-07-19",
    shoots: "L",
    rankings: { consensus: 10, eliteProspects: 9, tsnCraigButton: 11, bobMckenzie: 10, thehockeywriters: 9, fcHockey: 11 },
    trend: "stable",
    tier: "Top",
    scoutingNotes: "Smart two-way forward with excellent positioning. Strong defensive awareness for his age.",
  },
  {
    id: 11,
    name: "Cole Reschny",
    position: "C",
    team: "Victoria Royals",
    league: "WHL",
    nationality: "CAN",
    height: "5'11\"",
    weight: "175",
    birthDate: "2007-03-28",
    shoots: "L",
    rankings: { consensus: 11, eliteProspects: 12, tsnCraigButton: 10, bobMckenzie: 11, thehockeywriters: 11, fcHockey: 10 },
    trend: "up",
    tier: "First Round",
    scoutingNotes: "High-energy center with excellent work ethic. Dynamic skater with quick feet.",
  },
  {
    id: 12,
    name: "Blake Fiddler",
    position: "LD",
    team: "Edina High School",
    league: "USHS",
    nationality: "USA",
    height: "6'2\"",
    weight: "185",
    birthDate: "2007-02-14",
    shoots: "L",
    rankings: { consensus: 12, eliteProspects: 11, tsnCraigButton: 13, bobMckenzie: 12, thehockeywriters: 12, fcHockey: 12 },
    trend: "stable",
    tier: "First Round",
    scoutingNotes: "Mobile defenseman with excellent skating. Strong puck-moving ability and offensive upside.",
  },
  {
    id: 13,
    name: "Jack Murtagh",
    position: "C",
    team: "Youngstown Phantoms",
    league: "USHL",
    nationality: "USA",
    height: "6'0\"",
    weight: "180",
    birthDate: "2006-11-08",
    shoots: "R",
    rankings: { consensus: 13, eliteProspects: 14, tsnCraigButton: 12, bobMckenzie: 14, thehockeywriters: 13, fcHockey: 13 },
    trend: "down",
    tier: "First Round",
    scoutingNotes: "Competitive center with strong faceoff skills. Excellent in all three zones.",
  },
  {
    id: 14,
    name: "Ondřej Becher",
    position: "RW",
    team: "Prince George Cougars",
    league: "WHL",
    nationality: "CZE",
    height: "6'0\"",
    weight: "178",
    birthDate: "2006-06-25",
    shoots: "R",
    rankings: { consensus: 14, eliteProspects: 13, tsnCraigButton: 14, bobMckenzie: 13, thehockeywriters: 15, fcHockey: 14 },
    trend: "stable",
    tier: "First Round",
    scoutingNotes: "Skilled Czech winger with excellent shot. Good hockey sense and offensive awareness.",
  },
  {
    id: 15,
    name: "Nikita Artamonov",
    position: "LW",
    team: "Yaroslavl",
    league: "MHL",
    nationality: "RUS",
    height: "6'2\"",
    weight: "190",
    birthDate: "2007-01-05",
    shoots: "L",
    rankings: { consensus: 15, eliteProspects: 15, tsnCraigButton: 16, bobMckenzie: 15, thehockeywriters: 14, fcHockey: 16 },
    trend: "up",
    tier: "First Round",
    scoutingNotes: "Talented Russian forward with excellent size. Strong puck protection and finishing ability.",
  },
  {
    id: 16,
    name: "Kazden Warr",
    position: "RW",
    team: "Oshawa Generals",
    league: "OHL",
    nationality: "CAN",
    height: "6'1\"",
    weight: "182",
    birthDate: "2006-09-12",
    shoots: "R",
    rankings: { consensus: 16, eliteProspects: 16, tsnCraigButton: 15, bobMckenzie: 17, thehockeywriters: 16, fcHockey: 15 },
    trend: "stable",
    tier: "First Round",
    scoutingNotes: "Versatile forward with good size. Strong compete level and net-front presence.",
  },
  {
    id: 17,
    name: "Emil Järventie",
    position: "LW",
    team: "JYP",
    league: "Liiga",
    nationality: "FIN",
    height: "5'11\"",
    weight: "175",
    birthDate: "2007-04-03",
    shoots: "L",
    rankings: { consensus: 17, eliteProspects: 18, tsnCraigButton: 17, bobMckenzie: 16, thehockeywriters: 18, fcHockey: 17 },
    trend: "up",
    tier: "First Round",
    scoutingNotes: "Skilled Finnish winger with quick hands. Excellent offensive instincts and creativity.",
  },
  {
    id: 18,
    name: "Owen Allard",
    position: "C",
    team: "Drummondville Voltigeurs",
    league: "QMJHL",
    nationality: "CAN",
    height: "6'0\"",
    weight: "178",
    birthDate: "2006-10-17",
    shoots: "L",
    rankings: { consensus: 18, eliteProspects: 17, tsnCraigButton: 19, bobMckenzie: 18, thehockeywriters: 17, fcHockey: 18 },
    trend: "stable",
    tier: "First Round",
    scoutingNotes: "Playmaking center with excellent vision. Good skating and puck distribution.",
  },
  {
    id: 19,
    name: "Josh Humphreys",
    position: "RD",
    team: "Guelph Storm",
    league: "OHL",
    nationality: "CAN",
    height: "6'2\"",
    weight: "188",
    birthDate: "2007-02-28",
    shoots: "R",
    rankings: { consensus: 19, eliteProspects: 19, tsnCraigButton: 18, bobMckenzie: 20, thehockeywriters: 19, fcHockey: 20 },
    trend: "down",
    tier: "First Round",
    scoutingNotes: "Physical defenseman with improving offensive game. Strong defensive positioning.",
  },
  {
    id: 20,
    name: "Marek Vanacker",
    position: "LW",
    team: "Brantford Bulldogs",
    league: "OHL",
    nationality: "CAN",
    height: "6'3\"",
    weight: "200",
    birthDate: "2006-08-30",
    shoots: "L",
    rankings: { consensus: 20, eliteProspects: 20, tsnCraigButton: 20, bobMckenzie: 19, thehockeywriters: 20, fcHockey: 19 },
    trend: "stable",
    tier: "First Round",
    scoutingNotes: "Power forward with excellent size. Physical presence with improving skill set.",
  },
  {
    id: 21,
    name: "Danik Wisniewski",
    position: "C",
    team: "Green Bay Gamblers",
    league: "USHL",
    nationality: "USA",
    height: "5'11\"",
    weight: "172",
    birthDate: "2007-05-11",
    shoots: "L",
    rankings: { consensus: 21, eliteProspects: 22, tsnCraigButton: 21, bobMckenzie: 21, thehockeywriters: 22, fcHockey: 21 },
    trend: "up",
    tier: "Second Round",
    scoutingNotes: "Skilled American center with quick hands. Excellent offensive awareness.",
  },
  {
    id: 22,
    name: "Yegor Surin",
    position: "RW",
    team: "Loko Yaroslavl",
    league: "MHL",
    nationality: "RUS",
    height: "6'0\"",
    weight: "176",
    birthDate: "2006-12-22",
    shoots: "R",
    rankings: { consensus: 22, eliteProspects: 21, tsnCraigButton: 23, bobMckenzie: 22, thehockeywriters: 21, fcHockey: 23 },
    trend: "stable",
    tier: "Second Round",
    scoutingNotes: "Talented Russian winger with excellent shot. Good skating and puck skills.",
  },
  {
    id: 23,
    name: "Carsen Whynot",
    position: "LD",
    team: "Halifax Mooseheads",
    league: "QMJHL",
    nationality: "CAN",
    height: "6'1\"",
    weight: "182",
    birthDate: "2007-01-18",
    shoots: "L",
    rankings: { consensus: 23, eliteProspects: 24, tsnCraigButton: 22, bobMckenzie: 24, thehockeywriters: 23, fcHockey: 22 },
    trend: "up",
    tier: "Second Round",
    scoutingNotes: "Mobile defenseman with good offensive instincts. Strong skating and puck-moving ability.",
  },
  {
    id: 24,
    name: "Austin Burnevik",
    position: "RW",
    team: "Waterloo Black Hawks",
    league: "USHL",
    nationality: "USA",
    height: "6'0\"",
    weight: "180",
    birthDate: "2006-07-09",
    shoots: "R",
    rankings: { consensus: 24, eliteProspects: 23, tsnCraigButton: 24, bobMckenzie: 23, thehockeywriters: 24, fcHockey: 24 },
    trend: "stable",
    tier: "Second Round",
    scoutingNotes: "Skilled American winger with good hockey sense. Strong offensive production.",
  },
  {
    id: 25,
    name: "Tomas Lavoie",
    position: "RD",
    team: "Cape Breton Eagles",
    league: "QMJHL",
    nationality: "CAN",
    height: "6'4\"",
    weight: "205",
    birthDate: "2006-11-29",
    shoots: "R",
    rankings: { consensus: 25, eliteProspects: 25, tsnCraigButton: 25, bobMckenzie: 25, thehockeywriters: 25, fcHockey: 25 },
    trend: "stable",
    tier: "Second Round",
    scoutingNotes: "Big defenseman with excellent reach. Physical presence with developing offensive game.",
  },
  // European Prospects
  {
    id: 26,
    name: "Tuomas Uronen",
    position: "LW",
    team: "HIFK",
    league: "Liiga",
    nationality: "FIN",
    height: "6'1\"",
    weight: "185",
    birthDate: "2007-02-15",
    shoots: "L",
    rankings: { consensus: 26, eliteProspects: 27, tsnCraigButton: 26, bobMckenzie: 27, thehockeywriters: 26, fcHockey: 28 },
    trend: "up",
    tier: "Second Round",
    scoutingNotes: "Skilled Finnish winger with excellent speed. Strong offensive awareness and finishing ability.",
  },
  {
    id: 27,
    name: "Kasper Pikkarainen",
    position: "RW",
    team: "Kärpät",
    league: "Liiga",
    nationality: "FIN",
    height: "5'11\"",
    weight: "176",
    birthDate: "2007-03-22",
    shoots: "R",
    rankings: { consensus: 27, eliteProspects: 26, tsnCraigButton: 28, bobMckenzie: 26, thehockeywriters: 28, fcHockey: 26 },
    trend: "stable",
    tier: "Second Round",
    scoutingNotes: "Quick Finnish forward with excellent skating. Dangerous in transition with good hockey IQ.",
  },
  {
    id: 28,
    name: "Jakub Dvořák",
    position: "LD",
    team: "HC Sparta Praha",
    league: "Extraliga",
    nationality: "CZE",
    height: "6'3\"",
    weight: "195",
    birthDate: "2006-09-08",
    shoots: "L",
    rankings: { consensus: 28, eliteProspects: 29, tsnCraigButton: 27, bobMckenzie: 28, thehockeywriters: 27, fcHockey: 29 },
    trend: "up",
    tier: "Second Round",
    scoutingNotes: "Big Czech defenseman with excellent reach. Strong defensive game with improving offensive skills.",
  },
  {
    id: 29,
    name: "Linus Eriksson",
    position: "C",
    team: "Leksands IF",
    league: "SHL",
    nationality: "SWE",
    height: "6'0\"",
    weight: "180",
    birthDate: "2007-01-14",
    shoots: "L",
    rankings: { consensus: 29, eliteProspects: 28, tsnCraigButton: 30, bobMckenzie: 29, thehockeywriters: 29, fcHockey: 27 },
    trend: "stable",
    tier: "Second Round",
    scoutingNotes: "Smart Swedish center with excellent two-way game. Strong positional play and hockey sense.",
  },
  {
    id: 30,
    name: "Alexei Kolosov",
    position: "G",
    team: "Dinamo Minsk",
    league: "KHL",
    nationality: "BLR",
    height: "6'2\"",
    weight: "185",
    birthDate: "2006-08-30",
    shoots: "L",
    rankings: { consensus: 30, eliteProspects: 31, tsnCraigButton: 29, bobMckenzie: 30, thehockeywriters: 30, fcHockey: 30 },
    trend: "stable",
    tier: "Second Round",
    scoutingNotes: "Athletic goaltender with quick reflexes. Good positioning and excellent rebound control.",
  },
  {
    id: 31,
    name: "Theo Lindstein",
    position: "LD",
    team: "Brynäs IF",
    league: "SHL",
    nationality: "SWE",
    height: "6'1\"",
    weight: "183",
    birthDate: "2006-11-18",
    shoots: "L",
    rankings: { consensus: 31, eliteProspects: 30, tsnCraigButton: 32, bobMckenzie: 31, thehockeywriters: 31, fcHockey: 31 },
    trend: "up",
    tier: "Second Round",
    scoutingNotes: "Mobile Swedish defenseman with excellent puck skills. Strong skating and offensive instincts.",
  },
  {
    id: 32,
    name: "Maxim Strbák",
    position: "RD",
    team: "HK Nitra",
    league: "Slovak Extraliga",
    nationality: "SVK",
    height: "6'2\"",
    weight: "190",
    birthDate: "2007-04-12",
    shoots: "R",
    rankings: { consensus: 32, eliteProspects: 32, tsnCraigButton: 31, bobMckenzie: 33, thehockeywriters: 32, fcHockey: 32 },
    trend: "stable",
    tier: "Second Round",
    scoutingNotes: "Physical Slovak defenseman with good size. Strong defensive presence and improving mobility.",
  },
  {
    id: 33,
    name: "Julius Miettinen",
    position: "C",
    team: "Everett Silvertips",
    league: "WHL",
    nationality: "FIN",
    height: "6'3\"",
    weight: "195",
    birthDate: "2006-12-05",
    shoots: "L",
    rankings: { consensus: 33, eliteProspects: 33, tsnCraigButton: 33, bobMckenzie: 32, thehockeywriters: 34, fcHockey: 33 },
    trend: "stable",
    tier: "Second Round",
    scoutingNotes: "Big Finnish center with excellent reach. Strong in puck battles with good offensive upside.",
  },
  {
    id: 34,
    name: "Simon Skarzynski",
    position: "LW",
    team: "Adler Mannheim",
    league: "DEL",
    nationality: "GER",
    height: "5'11\"",
    weight: "175",
    birthDate: "2007-02-28",
    shoots: "L",
    rankings: { consensus: 34, eliteProspects: 35, tsnCraigButton: 34, bobMckenzie: 34, thehockeywriters: 33, fcHockey: 35 },
    trend: "up",
    tier: "Later",
    scoutingNotes: "Skilled German winger with quick hands. Dynamic skater with good offensive creativity.",
  },
  {
    id: 35,
    name: "Elias Salomonsson",
    position: "RD",
    team: "Skellefteå AIK",
    league: "SHL",
    nationality: "SWE",
    height: "6'0\"",
    weight: "178",
    birthDate: "2006-10-22",
    shoots: "R",
    rankings: { consensus: 35, eliteProspects: 34, tsnCraigButton: 36, bobMckenzie: 35, thehockeywriters: 35, fcHockey: 34 },
    trend: "stable",
    tier: "Later",
    scoutingNotes: "Puck-moving Swedish defenseman with excellent skating. Good offensive instincts and vision.",
  },
  {
    id: 36,
    name: "Daniil Orlov",
    position: "C",
    team: "SKA St. Petersburg",
    league: "KHL",
    nationality: "RUS",
    height: "6'1\"",
    weight: "182",
    birthDate: "2007-01-08",
    shoots: "L",
    rankings: { consensus: 36, eliteProspects: 36, tsnCraigButton: 35, bobMckenzie: 37, thehockeywriters: 36, fcHockey: 36 },
    trend: "up",
    tier: "Later",
    scoutingNotes: "Talented Russian center with excellent playmaking ability. Strong hockey sense and vision.",
  },
  {
    id: 37,
    name: "Patrik Bláha",
    position: "LW",
    team: "HC Oceláři Třinec",
    league: "Extraliga",
    nationality: "CZE",
    height: "5'10\"",
    weight: "170",
    birthDate: "2006-07-15",
    shoots: "L",
    rankings: { consensus: 37, eliteProspects: 38, tsnCraigButton: 37, bobMckenzie: 36, thehockeywriters: 38, fcHockey: 37 },
    trend: "stable",
    tier: "Later",
    scoutingNotes: "Skilled Czech winger with quick acceleration. Excellent offensive instincts and creativity.",
  },
  {
    id: 38,
    name: "Axel Hurtig",
    position: "RW",
    team: "Färjestad BK",
    league: "SHL",
    nationality: "SWE",
    height: "6'2\"",
    weight: "188",
    birthDate: "2007-03-01",
    shoots: "R",
    rankings: { consensus: 38, eliteProspects: 37, tsnCraigButton: 38, bobMckenzie: 38, thehockeywriters: 37, fcHockey: 38 },
    trend: "stable",
    tier: "Later",
    scoutingNotes: "Big Swedish winger with good size. Strong along the boards with improving finishing.",
  },
  {
    id: 39,
    name: "Veeti Vaisanen",
    position: "LD",
    team: "TPS",
    league: "Liiga",
    nationality: "FIN",
    height: "6'1\"",
    weight: "185",
    birthDate: "2006-06-20",
    shoots: "L",
    rankings: { consensus: 39, eliteProspects: 39, tsnCraigButton: 40, bobMckenzie: 39, thehockeywriters: 39, fcHockey: 39 },
    trend: "up",
    tier: "Later",
    scoutingNotes: "Mobile Finnish defenseman with good skating. Strong two-way game and puck distribution.",
  },
  {
    id: 40,
    name: "Leon Muggli",
    position: "C",
    team: "ZSC Lions",
    league: "NL",
    nationality: "CHE",
    height: "5'11\"",
    weight: "175",
    birthDate: "2007-04-18",
    shoots: "L",
    rankings: { consensus: 40, eliteProspects: 40, tsnCraigButton: 39, bobMckenzie: 40, thehockeywriters: 40, fcHockey: 40 },
    trend: "stable",
    tier: "Later",
    scoutingNotes: "Skilled Swiss center with excellent hockey IQ. Good playmaking ability and vision.",
  },
  {
    id: 41,
    name: "Erik Hedlund",
    position: "G",
    team: "Modo Hockey",
    league: "HockeyAllsvenskan",
    nationality: "SWE",
    height: "6'3\"",
    weight: "190",
    birthDate: "2006-09-30",
    shoots: "L",
    rankings: { consensus: 41, eliteProspects: 41, tsnCraigButton: 41, bobMckenzie: 41, thehockeywriters: 41, fcHockey: 41 },
    trend: "up",
    tier: "Later",
    scoutingNotes: "Athletic Swedish goalie with excellent size. Quick reflexes and good positioning.",
  },
  {
    id: 42,
    name: "Moritz Seider Jr.",
    position: "RD",
    team: "Eisbären Berlin",
    league: "DEL",
    nationality: "GER",
    height: "6'4\"",
    weight: "200",
    birthDate: "2007-01-25",
    shoots: "R",
    rankings: { consensus: 42, eliteProspects: 42, tsnCraigButton: 42, bobMckenzie: 42, thehockeywriters: 42, fcHockey: 42 },
    trend: "stable",
    tier: "Later",
    scoutingNotes: "Big German defenseman with excellent reach. Physical presence with solid defensive positioning.",
  },
  {
    id: 43,
    name: "Oliver Kapanen",
    position: "C",
    team: "KalPa",
    league: "Liiga",
    nationality: "FIN",
    height: "6'0\"",
    weight: "178",
    birthDate: "2006-11-12",
    shoots: "L",
    rankings: { consensus: 43, eliteProspects: 43, tsnCraigButton: 44, bobMckenzie: 43, thehockeywriters: 43, fcHockey: 43 },
    trend: "up",
    tier: "Later",
    scoutingNotes: "Smart Finnish center with excellent hockey sense. Good two-way game and positioning.",
  },
  {
    id: 44,
    name: "David Reinbacher Jr.",
    position: "LD",
    team: "EHC Kloten",
    league: "NL",
    nationality: "CHE",
    height: "6'2\"",
    weight: "188",
    birthDate: "2007-02-08",
    shoots: "L",
    rankings: { consensus: 44, eliteProspects: 44, tsnCraigButton: 43, bobMckenzie: 44, thehockeywriters: 44, fcHockey: 44 },
    trend: "stable",
    tier: "Later",
    scoutingNotes: "Swiss defenseman with excellent skating. Good puck-moving ability and defensive awareness.",
  },
  {
    id: 45,
    name: "Adam Jiříček Jr.",
    position: "RD",
    team: "HC Plzeň",
    league: "Extraliga",
    nationality: "CZE",
    height: "6'3\"",
    weight: "195",
    birthDate: "2006-08-05",
    shoots: "R",
    rankings: { consensus: 45, eliteProspects: 45, tsnCraigButton: 45, bobMckenzie: 45, thehockeywriters: 45, fcHockey: 45 },
    trend: "stable",
    tier: "Later",
    scoutingNotes: "Big Czech defenseman with family pedigree. Strong defensive presence with offensive upside.",
  },
];

const scoutSources = [
  { id: "consensus", name: "Consensus", description: "Average of all scouts" },
  { id: "eliteProspects", name: "Elite Prospects", description: "EP Rinkside" },
  { id: "tsnCraigButton", name: "TSN Craig Button", description: "TSN Analyst" },
  { id: "bobMckenzie", name: "Bob McKenzie", description: "TSN Insider" },
  { id: "thehockeywriters", name: "The Hockey Writers", description: "THW Staff" },
  { id: "fcHockey", name: "FC Hockey", description: "FC Hockey Staff" },
];

const getFlagEmoji = (country: string): string => {
  const flags: Record<string, string> = {
    CAN: "🇨🇦",
    USA: "🇺🇸",
    SWE: "🇸🇪",
    FIN: "🇫🇮",
    RUS: "🇷🇺",
    CZE: "🇨🇿",
    SVK: "🇸🇰",
    GER: "🇩🇪",
    CHE: "🇨🇭",
    BLR: "🇧🇾",
    AUT: "🇦🇹",
    DEN: "🇩🇰",
    NOR: "🇳🇴",
    LAT: "🇱🇻",
  };
  return flags[country] || "🏒";
};

const getTierColor = (tier: string): string => {
  switch (tier) {
    case "Elite":
      return "bg-primary/20 text-primary border-primary/30";
    case "Top":
      return "bg-amber-500/20 text-amber-500 border-amber-500/30";
    case "First Round":
      return "bg-emerald-500/20 text-emerald-500 border-emerald-500/30";
    case "Second Round":
      return "bg-blue-500/20 text-blue-500 border-blue-500/30";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
};

const DraftRankings = () => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<SortField>("consensus");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [positionFilter, setPositionFilter] = useState<string>("all");
  const [leagueFilter, setLeagueFilter] = useState<string>("all");
  const [tierFilter, setTierFilter] = useState<string>("all");
  const [selectedProspect, setSelectedProspect] = useState<Prospect | null>(null);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedProspects = useMemo(() => {
    let filtered = [...prospects];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.team.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply position filter
    if (positionFilter !== "all") {
      filtered = filtered.filter(p => p.position === positionFilter);
    }

    // Apply league filter
    if (leagueFilter !== "all") {
      filtered = filtered.filter(p => p.league === leagueFilter);
    }

    // Apply tier filter
    if (tierFilter !== "all") {
      filtered = filtered.filter(p => p.tier === tierFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue: number | string;
      let bValue: number | string;

      if (sortField === "name") {
        aValue = a.name;
        bValue = b.name;
      } else {
        aValue = a.rankings[sortField];
        bValue = b.rankings[sortField];
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortDirection === "asc"
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

    return filtered;
  }, [searchQuery, positionFilter, leagueFilter, tierFilter, sortField, sortDirection]);

  const SortHeader = ({ field, label }: { field: SortField; label: string }) => (
    <TableHead
      className="text-muted-foreground cursor-pointer hover:text-foreground transition-colors text-center"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center justify-center gap-1">
        <span>{label}</span>
        {sortField === field ? (
          sortDirection === "asc" ? (
            <ArrowUp className="w-3 h-3" />
          ) : (
            <ArrowDown className="w-3 h-3" />
          )
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-50" />
        )}
      </div>
    </TableHead>
  );

  const uniqueLeagues = [...new Set(prospects.map(p => p.league))];
  const uniquePositions = [...new Set(prospects.map(p => p.position))];

  return (
    <div className="min-h-screen bg-background pt-16">
      <Navbar />
      <main className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="mb-4 gap-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-display text-4xl font-bold text-foreground flex items-center gap-3">
                <Star className="w-8 h-8 text-primary" />
                2025 NHL Draft Rankings
              </h1>
              <p className="text-muted-foreground mt-2">
                Compare prospect rankings from top scouts and analysts
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="px-3 py-1">
                <Users className="w-4 h-4 mr-1" />
                {prospects.length} Prospects
              </Badge>
              <Badge variant="outline" className="px-3 py-1 bg-primary/10 text-primary border-primary/30">
                <Sparkles className="w-4 h-4 mr-1" />
                Updated Jan 2026
              </Badge>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="glass-card p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search prospects or teams..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <Select value={positionFilter} onValueChange={setPositionFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Position" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Positions</SelectItem>
                  {uniquePositions.map(pos => (
                    <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={leagueFilter} onValueChange={setLeagueFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="League" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Leagues</SelectItem>
                  {uniqueLeagues.map(league => (
                    <SelectItem key={league} value={league}>{league}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={tierFilter} onValueChange={setTierFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="Elite">Elite</SelectItem>
                  <SelectItem value="Top">Top</SelectItem>
                  <SelectItem value="First Round">First Round</SelectItem>
                  <SelectItem value="Second Round">Second Round</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Scout Sources Legend */}
        <div className="glass-card p-4 mb-6">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-primary" />
            Scout Sources
          </h3>
          <div className="flex flex-wrap gap-4">
            {scoutSources.map(source => (
              <div key={source.id} className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span className="text-sm font-medium text-foreground">{source.name}</span>
                <span className="text-xs text-muted-foreground">({source.description})</span>
              </div>
            ))}
          </div>
        </div>

        {/* Main Rankings Table */}
        <Tabs defaultValue="rankings" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="rankings" className="flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4" />
              Rankings Board
            </TabsTrigger>
            <TabsTrigger value="comparison" className="flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Scout Comparison
            </TabsTrigger>
          </TabsList>

          <TabsContent value="rankings">
            <div className="glass-card overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50 hover:bg-transparent">
                      <SortHeader field="consensus" label="#" />
                      <TableHead className="text-muted-foreground">Prospect</TableHead>
                      <TableHead className="text-muted-foreground text-center">Pos</TableHead>
                      <TableHead className="text-muted-foreground">Team</TableHead>
                      <TableHead className="text-muted-foreground text-center">Tier</TableHead>
                      <TableHead className="text-muted-foreground text-center">Trend</TableHead>
                      <SortHeader field="eliteProspects" label="EP" />
                      <SortHeader field="tsnCraigButton" label="TSN" />
                      <SortHeader field="bobMckenzie" label="Bob" />
                      <SortHeader field="thehockeywriters" label="THW" />
                      <SortHeader field="fcHockey" label="FC" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedProspects.map((prospect) => (
                      <TableRow
                        key={prospect.id}
                        className="border-border/30 hover:bg-muted/30 cursor-pointer"
                        onClick={() => setSelectedProspect(selectedProspect?.id === prospect.id ? null : prospect)}
                      >
                        <TableCell className="font-mono font-bold text-primary text-center">
                          {prospect.rankings.consensus}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{getFlagEmoji(prospect.nationality)}</span>
                            <div>
                              <span className="font-medium text-foreground">{prospect.name}</span>
                              <p className="text-xs text-muted-foreground">{prospect.height} • {prospect.weight} lbs</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="font-mono">{prospect.position}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-foreground">{prospect.team}</span>
                            <span className="text-xs text-muted-foreground">{prospect.league}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className={getTierColor(prospect.tier)}>
                            {prospect.tier}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {prospect.trend === "up" && (
                            <TrendingUp className="w-4 h-4 text-score-win mx-auto" />
                          )}
                          {prospect.trend === "down" && (
                            <TrendingDown className="w-4 h-4 text-score-loss mx-auto" />
                          )}
                          {prospect.trend === "stable" && (
                            <div className="w-4 h-0.5 bg-muted-foreground mx-auto" />
                          )}
                        </TableCell>
                        <TableCell className="text-center font-mono">{prospect.rankings.eliteProspects}</TableCell>
                        <TableCell className="text-center font-mono">{prospect.rankings.tsnCraigButton}</TableCell>
                        <TableCell className="text-center font-mono">{prospect.rankings.bobMckenzie}</TableCell>
                        <TableCell className="text-center font-mono">{prospect.rankings.thehockeywriters}</TableCell>
                        <TableCell className="text-center font-mono">{prospect.rankings.fcHockey}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Selected Prospect Detail */}
            {selectedProspect && (
              <div className="mt-6 glass-card p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl bg-primary/20 flex items-center justify-center text-3xl">
                      {getFlagEmoji(selectedProspect.nationality)}
                    </div>
                    <div>
                      <h3 className="font-display text-2xl font-bold text-foreground">{selectedProspect.name}</h3>
                      <p className="text-muted-foreground">{selectedProspect.team} • {selectedProspect.league}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-lg px-4 py-2 ${getTierColor(selectedProspect.tier)}`}>
                    #{selectedProspect.rankings.consensus} Overall
                  </Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-6">
                  <div className="bg-muted/30 rounded-xl p-3 text-center">
                    <p className="text-xs text-muted-foreground">Position</p>
                    <p className="font-bold text-foreground">{selectedProspect.position}</p>
                  </div>
                  <div className="bg-muted/30 rounded-xl p-3 text-center">
                    <p className="text-xs text-muted-foreground">Height</p>
                    <p className="font-bold text-foreground">{selectedProspect.height}</p>
                  </div>
                  <div className="bg-muted/30 rounded-xl p-3 text-center">
                    <p className="text-xs text-muted-foreground">Weight</p>
                    <p className="font-bold text-foreground">{selectedProspect.weight} lbs</p>
                  </div>
                  <div className="bg-muted/30 rounded-xl p-3 text-center">
                    <p className="text-xs text-muted-foreground">Shoots</p>
                    <p className="font-bold text-foreground">{selectedProspect.shoots}</p>
                  </div>
                  <div className="bg-muted/30 rounded-xl p-3 text-center">
                    <p className="text-xs text-muted-foreground">Birth Date</p>
                    <p className="font-bold text-foreground">{selectedProspect.birthDate}</p>
                  </div>
                  <div className="bg-muted/30 rounded-xl p-3 text-center">
                    <p className="text-xs text-muted-foreground">Nationality</p>
                    <p className="font-bold text-foreground">{selectedProspect.nationality}</p>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-muted/20 rounded-xl border border-border/50">
                  <h4 className="font-semibold text-foreground mb-2">Scouting Report</h4>
                  <p className="text-muted-foreground">{selectedProspect.scoutingNotes}</p>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="comparison">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {scoutSources.map(source => (
                <div key={source.id} className="glass-card p-4">
                  <h4 className="font-display font-bold text-foreground mb-1">{source.name}</h4>
                  <p className="text-xs text-muted-foreground mb-4">{source.description}</p>
                  <div className="space-y-2">
                    {[...prospects]
                      .sort((a, b) => a.rankings[source.id as keyof typeof a.rankings] - b.rankings[source.id as keyof typeof b.rankings])
                      .slice(0, 10)
                      .map((prospect, index) => (
                        <div
                          key={prospect.id}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <span className="font-mono font-bold text-primary w-6">{index + 1}</span>
                          <span className="text-lg">{getFlagEmoji(prospect.nationality)}</span>
                          <span className="flex-1 font-medium text-foreground text-sm">{prospect.name}</span>
                          <Badge variant="outline" className="text-xs">{prospect.position}</Badge>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
};

export default DraftRankings;
